// src/components/system/config/websiteConfig.js
// Website Configuration System
// Created: 2025-01-20 16:45:00 UTC+8
// Purpose: Centralized configuration system for website settings, SEO, and i18n

/**
 * Default website configuration schema
 * This configuration allows users/admins to create customized websites for different customers
 */
export const defaultWebsiteConfig = {
  // Basic Website Information
  website: {
    title: "HONGKONG.AI",
    subtitle: "To leverage A.I. for the improvement of people's lives and the benefit of society.",
    description: "To leverage A.I. for the improvement of people's lives and the benefit of society.",
    url: "https://hongkong.ai",
    logo: "/logo_with_text.223bef95.svg",
    favicon: "/ico/ai_color.svg",
    version: "1.0.0",
    lastUpdated: new Date().toISOString()
  },

  // SEO Configuration
  seo: {
    // Meta Tags
    meta: {
      keywords: "Artificial Intelligence, AI, Society, improvement, people's lives, Ethical AI, Hong Kong AI, benefit of society",
      author: "HONGKONG.AI Team",
      robots: "index, follow",
      viewport: "width=device-width, initial-scale=1.0",
      charset: "UTF-8",
      language: "en",
      revisitAfter: "7 days",
      distribution: "global",
      rating: "general"
    },

    // Open Graph Tags
    openGraph: {
      type: "website",
      siteName: "HONGKONG.AI",
      title: "HONGKONG.AI - Empowering Hong Kong's Future with AI",
      description: "To leverage A.I. for the improvement of people's lives and the benefit of society.",
      image: "/HK_1920x1080.jpg",
      imageAlt: "HONGKONG.AI - AI for Social Good",
      url: "https://hongkong.ai",
      locale: "en_US"
    },

    // Twitter Card
    twitter: {
      card: "summary_large_image",
      site: "@hongkongai",
      creator: "@hongkongai",
      title: "HONGKONG.AI - Empowering Hong Kong's Future with AI",
      description: "To leverage A.I. for the improvement of people's lives and the benefit of society.",
      image: "/HK_1920x1080.jpg"
    },

    // Structured Data (JSON-LD)
    structuredData: {
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": "HONGKONG.AI",
      "url": "https://hongkong.ai",
      "logo": "https://hongkong.ai/logo_with_text.223bef95.svg",
      "description": "To leverage A.I. for the improvement of people's lives and the benefit of society.",
      "foundingDate": "2024",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Hong Kong",
        "addressCountry": "HK"
      },
      "sameAs": [
        "https://twitter.com/hongkongai",
        "https://linkedin.com/company/hongkongai"
      ]
    },

    // Security Headers
    security: {
      contentSecurityPolicy: "default-src 'self'; script-src 'self' 'unsafe-inline' 'unsafe-eval'; style-src 'self' 'unsafe-inline';",
      xFrameOptions: "DENY",
      xContentTypeOptions: "nosniff",
      referrerPolicy: "strict-origin-when-cross-origin"
    }
  },

  // Internationalization Configuration
  i18n: {
    defaultLanguage: "en",
    supportedLanguages: ["en", "sc", "tc", "th"],
    fallbackLanguage: "en",
    languageNames: {
      en: "English",
      sc: "简体中文",
      tc: "繁體中文",
      th: "ไทย"
    },
    rtlLanguages: [],
    dateFormat: {
      en: "MM/DD/YYYY",
      sc: "YYYY年MM月DD日",
      tc: "YYYY年MM月DD日",
      th: "DD/MM/YYYY"
    },
    numberFormat: {
      en: "en-US",
      sc: "zh-CN",
      tc: "zh-TW",
      th: "th-TH"
    }
  },

  // Theme Configuration
  theme: {
    primaryColor: "#000000",
    secondaryColor: "#ffffff",
    successColor: "#28a745",
    dangerColor: "#dc3545",
    warningColor: "#ffc107",
    infoColor: "#17a2b8",
    lightColor: "#f8f9fa",
    darkColor: "#343a40",
    fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
    fontSize: "16px",
    lineHeight: "1.5",
    exhibitorsLayout: "card"
  },

  // Navigation Configuration
  navigation: {
    header: {
      showLogo: true,
      showLanguageSelector: true,
      showSearch: false,
      sticky: true
    },
    footer: {
      showSocialLinks: true,
      showCopyright: true,
      showPrivacyPolicy: true,
      showTermsOfUse: true
    },
    menu: [
      { key: "home", path: "/", titleKey: "Home", order: 1 },
      { key: "news", path: "/news", titleKey: "Latest News", order: 2 },
      { key: "exhibitors", path: "/Exhibitors", titleKey: "Exhibitors", order: 3 },
      { key: "ai4good", path: "/AI4Good", titleKey: "AI4Good", order: 4 },
      { key: "aboutus", path: "/aboutus", titleKey: "About Us", order: 5 },
      { key: "project", path: "/project", titleKey: "Project", order: 6 }
    ]
  },

  // Analytics Configuration
  analytics: {
    googleAnalytics: {
      enabled: false,
      trackingId: ""
    },
    googleTagManager: {
      enabled: false,
      containerId: ""
    },
    facebookPixel: {
      enabled: false,
      pixelId: ""
    }
  },

  // Performance Configuration
  performance: {
    lazyLoading: true,
    imageOptimization: true,
    caching: {
      enabled: true,
      duration: 3600000 // 1 hour in milliseconds
    },
    compression: {
      enabled: true,
      level: 6
    }
  },

  // Contact Information
  contact: {
    email: "enquiry@hongkong.ai",
    phone: "",
    address: "Hong Kong",
    socialMedia: {
      twitter: "https://twitter.com/hongkongai",
      linkedin: "https://linkedin.com/company/hongkongai",
      facebook: "",
      instagram: ""
    }
  },

  // Feature Flags
  features: {
    cookieConsent: true,
    newsletter: false,
    comments: false,
    search: false,
    darkMode: false,
    blackWhiteMode: false,
    multiLanguage: true,
    serviceWorker: true
  }
};

/**
 * Configuration validation schema
 */
export const configValidationRules = {
  website: {
    title: { required: true, type: 'string', maxLength: 100 },
    description: { required: true, type: 'string', maxLength: 300 },
    url: { required: true, type: 'string', pattern: /^https?:\/\/.+/ }
  },
  seo: {
    meta: {
      keywords: { required: true, type: 'string', maxLength: 500 }
    }
  },
  i18n: {
    defaultLanguage: { required: true, type: 'string', enum: ['en', 'sc', 'tc', 'th'] },
    supportedLanguages: { required: true, type: 'array', minLength: 1 }
  }
};

export default defaultWebsiteConfig;
